import { InfoOutline } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import FullHeightView from 'components/new/FullHeightView';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import TabsPanel from 'further-ui/components/TabsPanel';
import { Column, Row } from 'further-ui/components/Layout';
import { revaluation as rules } from 'further-ui/validations';
import { useNotification } from 'hooks/ui/useNotification';
import { ReactNode, useEffect } from 'react';
import { FieldErrors, useFormContext } from 'react-hook-form';
import { RevaluationExternalState } from '../FormWrapper';
import useRevaluations from '../hooks/useRevaluations';
import {
  updateAllNewRevaluationsDate,
  updateAllNewRevaluationsUnitPrice,
} from '../hooks/useRevaluationFormHandlers/newRevaluationBulkUpdates';
import HoldingClassRevaluationSection from './HoldingClassRevaluationSection';
import OverallRevaluationSection from './OverallRevaluationSection';
import { HoldingClassInvestmentTotal } from './types';

type RevaluationTab = 'total' | 'perClass';

type RevaluationFormProps = {
  assetName: string;
  assetId: string;
  assetCurrency: CurrencyCode;
  vehicleId: string;
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    fxDataFeed: FxDataFeed;
  };
  holdingClassInvestmentTotals: Array<HoldingClassInvestmentTotal>;
  onSuccess: (data: rules.RevaluationFormType) => void;
  renderButtons?: (handlers: {
    onSubmit: (data: rules.RevaluationFormType) => void;
    onError: (errors: FieldErrors<rules.RevaluationFormType>) => void;
    handleSubmit: (
      onValid: (data: rules.RevaluationFormType) => void,
      onInvalid?: (errors: FieldErrors<rules.RevaluationFormType>) => void,
    ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
  }) => ReactNode;
  headerContent?: ReactNode;
  viewOnly?: boolean;
  tabbed?: boolean;
  initialTab?: RevaluationTab;
  onTabChange?: (tab: RevaluationTab) => void;
  fxRate: CurrencyRateDetails | null;
  rateSelector?: ReactNode;
  disableDate?: boolean;
};

const RevaluationForm: React.FC<RevaluationFormProps> = ({
  assetName,
  assetId,
  assetCurrency,
  vehicleId,
  vehicle,
  holdingClassInvestmentTotals,
  onSuccess,
  renderButtons,
  headerContent,
  viewOnly,
  tabbed,
  initialTab,
  onTabChange,
  fxRate,
  rateSelector,
  disableDate,
}) => {
  const { handleSubmit } = useFormContext<rules.RevaluationFormType>();

  const { formMethods } = useFormWithStateContext<
    rules.RevaluationFormType,
    RevaluationExternalState
  >();

  const { watch, control, getValues, setValue } = formMethods;

  const { getAllRevaluations } = useRevaluations({
    watch,
    control,
    getValues,
  });

  const newRevaluations = getAllRevaluations().filter(
    (rev) => rev.isNew && !rev.isRemoved,
  );

  const currency = assetCurrency;
  const isNonLcy = currency && currency !== vehicle.baseCurrency;

  useEffect(() => {
    if (fxRate) {
      setValue('revaluationCurrencyRates.global', fxRate);
    }
  }, [fxRate, setValue]);

  const notification = useNotification();

  const onSubmit = (data: rules.RevaluationFormType) => {
    if (newRevaluations.length === 0) {
      notification.error('No new revaluations found.');
      return;
    }

    onSuccess(data);
  };

  const onError = (errors: FieldErrors<rules.RevaluationFormType>) => {
    notification.error(
      errors.newRevaluations?.root?.message ?? 'Something went wrong.',
    );
  };

  const handleTabChange = (tabKey: RevaluationTab) => {
    onTabChange?.(tabKey);

    if (tabKey === 'total') {
      const currentUnitPrice = getValues('currentUnitPrice') ?? 0;
      const currentRevaluationDate =
        getValues('currentRevaluationDate') ?? new Date();
      const currentNewRevaluations = getValues('newRevaluations');
      const investmentHistory = getValues('investmentHistory') ?? [];

      if (currentUnitPrice > 0) {
        updateAllNewRevaluationsUnitPrice(
          currentUnitPrice,
          currentNewRevaluations,
          investmentHistory,
          setValue,
        );
      }
      updateAllNewRevaluationsDate(
        currentRevaluationDate,
        currentNewRevaluations,
        setValue,
      );
    }
  };

  if (tabbed) {
    return (
      <TabsPanel
        keepAlive
        contentSpacing="none"
        contentPadding="sm"
        defaultTab={initialTab ?? 'total'}
        onTabChange={handleTabChange}
        tabs={[
          {
            key: 'total',
            label: (
              <Row centered spacing="xxs">
                Total asset revaluation
                <Tooltip title="Total asset revaluation should only be used when the unit price is the same across all holding classes.">
                  <InfoOutline fontSize="inherit" />
                </Tooltip>
              </Row>
            ),
            content: (
              <OverallRevaluationSection
                assetName={assetName}
                holdingClassInvestmentTotals={holdingClassInvestmentTotals}
                viewOnly={viewOnly}
                disableBorder
                isNonLcy={!!isNonLcy}
                vehicleBaseCurrency={vehicle.baseCurrency}
                fxRate={fxRate}
                disableDate={disableDate}
              />
            ),
          },
          {
            key: 'perClass',
            label: 'Per class revaluation',
            content: (
              <HoldingClassRevaluationSection
                assetId={assetId}
                vehicleId={vehicleId}
                viewOnly={viewOnly}
                disableBorder
                simpleHeader
                isNonLcy={!!isNonLcy}
                vehicleBaseCurrency={vehicle.baseCurrency}
                fxRate={fxRate}
                disableDate={disableDate}
              />
            ),
          },
        ]}
      />
    );
  }

  return (
    <FullHeightView
      actionBar={
        renderButtons
          ? renderButtons({ onSubmit, onError, handleSubmit })
          : null
      }
    >
      <Column spacing="lg">
        {headerContent}
        <OverallRevaluationSection
          assetName={assetName}
          holdingClassInvestmentTotals={holdingClassInvestmentTotals}
          viewOnly={viewOnly}
          isNonLcy={!!isNonLcy}
          vehicleBaseCurrency={vehicle.baseCurrency}
          fxRate={fxRate}
          disableDate={disableDate}
        />
      </Column>

      {isNonLcy && rateSelector}
      <HoldingClassRevaluationSection
        assetId={assetId}
        vehicleId={vehicleId}
        viewOnly={viewOnly}
        isNonLcy={!!isNonLcy}
        vehicleBaseCurrency={vehicle.baseCurrency}
        fxRate={fxRate}
        disableDate={disableDate}
      />
    </FullHeightView>
  );
};

export default RevaluationForm;
